import { z } from 'zod';
import type { CurrencyAmount } from '../money';
import {
  amountSchema,
  currencySchema,
  emailSchema,
  idSchema,
  localDateSchema,
  markdownSchema,
  nameSchema,
  urlSchema,
  versionSchema,
} from './common';
import { type BillingCycle, billingCycleSchema, type ServiceDto } from './services';

/**
 * Utrzymanie (docs/MODULE_UTRZYMANIE.md, docs/DATA_MODEL.md „maintenance + vault”):
 * `hosted_assets`, `asset_components`, `supplier_renewals`, `renewal_payments`, `reminder_rules`,
 * `reminder_log`, `credentials` (packages/db/src/schema/maintenance.ts). Moduł uprawnień: `maintenance`;
 * sejf (`credentials`, sekrety) osobno: `ctx.access.canSeeVault` + step-up (Fala 3).
 * Umowa utrzymania = usługa (`services`, `kind: 'maintenance'`), patrz contracts/services.ts.
 * Wejścia i wyjścia usług dopisuje agent modułu maintenance.
 */

// ── Zasoby ──────────────────────────────────────────────────────────────────
export const ASSET_TYPES = ['website', 'app', 'domain', 'mailbox', 'server', 'other'] as const;
export type AssetType = (typeof ASSET_TYPES)[number];
export const assetTypeSchema = z.enum(ASSET_TYPES);

/** ok = działa (monitoring), alarm, active = aktywna (domena, skrzynka), disabled = wyłączona, expired = wygasła. */
export const ASSET_STATUSES = ['ok', 'alarm', 'active', 'disabled', 'expired'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];
export const assetStatusSchema = z.enum(ASSET_STATUSES);

export const ASSET_COMPONENT_KINDS = ['hosting', 'domain', 'ssl', 'database', 'mailbox', 'other'] as const;
export type AssetComponentKind = (typeof ASSET_COMPONENT_KINDS)[number];
export const assetComponentKindSchema = z.enum(ASSET_COMPONENT_KINDS);

export const hostedAssetFieldsSchema = z.object({
  type: assetTypeSchema,
  /** nazwa albo adres widoczny na liście, np. „helpmycar.pl”, „kontakt@rea.pl” */
  name: nameSchema,
  address: z.string().trim().max(500).nullable().optional(),
  clientId: idSchema,
  projectId: idSchema.nullable().optional(),
  serviceId: idSchema.nullable().optional(),
  /** gdzie stoi: dostawca (np. „Zenbox”) i serwer (np. „s55”) */
  provider: z.string().trim().max(100).nullable().optional(),
  server: z.string().trim().max(100).nullable().optional(),
  tech: z.string().trim().max(200).nullable().optional(),
  repoUrl: urlSchema.nullable().optional(),
  monitorId: idSchema.nullable().optional(),
  status: assetStatusSchema.default('active'),
  notesMd: markdownSchema.nullable().optional(),
});
export type HostedAssetFields = z.infer<typeof hostedAssetFieldsSchema>;

export const assetComponentFieldsSchema = z.object({
  assetId: idSchema,
  kind: assetComponentKindSchema,
  description: z.string().trim().max(500).nullable().optional(),
  provider: z.string().trim().max(100).nullable().optional(),
  expiresOn: localDateSchema.nullable().optional(),
  /** w pakiecie (true) albo płatny osobno (false; koszt w `supplier_renewals`) */
  included: z.boolean().default(true),
});
export type AssetComponentFields = z.infer<typeof assetComponentFieldsSchema>;

// ── Koszty dostawców i płatności ────────────────────────────────────────────
export const supplierRenewalFieldsSchema = z.object({
  assetId: idSchema,
  /** element zasobu, którego dotyczy koszt (np. domena) */
  componentId: idSchema.nullable().optional(),
  provider: z.string().trim().min(1).max(100),
  amount: amountSchema,
  currency: currencySchema.default('PLN'),
  cycle: billingCycleSchema.default('year'),
  renewsOn: localDateSchema,
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type SupplierRenewalFields = z.infer<typeof supplierRenewalFieldsSchema>;

export const RENEWAL_PAYMENT_STATUSES = ['pending', 'invoiced', 'paid', 'overdue'] as const;
export type RenewalPaymentStatus = (typeof RENEWAL_PAYMENT_STATUSES)[number];
export const renewalPaymentStatusSchema = z.enum(RENEWAL_PAYMENT_STATUSES);

export const renewalPaymentFieldsSchema = z.object({
  serviceId: idSchema,
  periodStart: localDateSchema,
  periodEnd: localDateSchema,
  amount: amountSchema,
  currency: currencySchema.default('PLN'),
  status: renewalPaymentStatusSchema.default('pending'),
  dueDate: localDateSchema,
});
export type RenewalPaymentFields = z.infer<typeof renewalPaymentFieldsSchema>;

// ── Przypomnienia ───────────────────────────────────────────────────────────
/** Cel reguły: umowa (usługa) albo koszt dostawcy. W bazie: `service_id` albo `supplier_renewal_id`. */
export const REMINDER_TARGET_TYPES = ['service', 'supplier_renewal'] as const;
export type ReminderTargetType = (typeof REMINDER_TARGET_TYPES)[number];
export const reminderTargetTypeSchema = z.enum(REMINDER_TARGET_TYPES);

/** owner = opiekun / zespół, client = klient (mail zawsze przez Akceptacje, chyba że automat w umowie). */
export const REMINDER_AUDIENCES = ['owner', 'client'] as const;
export type ReminderAudience = (typeof REMINDER_AUDIENCES)[number];
export const reminderAudienceSchema = z.enum(REMINDER_AUDIENCES);

export const REMINDER_CHANNELS = ['today', 'inbox', 'push', 'email_approval'] as const;
export type ReminderChannel = (typeof REMINDER_CHANNELS)[number];
export const reminderChannelSchema = z.enum(REMINDER_CHANNELS);

/** Dni względem terminu: ujemne = przed, dodatnie = po (np. [-30, -7] albo [7, 14, 21]). */
export const reminderOffsetsSchema = z
  .array(z.number().int().min(-365).max(365))
  .min(1, { message: 'Podaj co najmniej jeden dzień' })
  .max(20);

const reminderRuleFieldsBase = z.object({
  targetType: reminderTargetTypeSchema,
  /** ustawione dokładnie jedno, zgodnie z `targetType` (CHECK w bazie) */
  serviceId: idSchema.nullable().optional(),
  supplierRenewalId: idSchema.nullable().optional(),
  audience: reminderAudienceSchema,
  offsetsDays: reminderOffsetsSchema,
  channel: reminderChannelSchema,
  /** najwyżej tyle przypomnień po terminie (null = wszystkie z `offsetsDays`) */
  maxRepeats: z.number().int().positive().max(50).nullable().optional(),
  enabled: z.boolean().default(true),
});
export type ReminderRuleFields = z.infer<typeof reminderRuleFieldsBase>;

/** Cel reguły zgodny z `targetType` (ta sama reguła: CHECK `reminder_rules_target_check`). */
export function reminderRuleRefinement(
  v: Partial<Pick<ReminderRuleFields, 'targetType' | 'serviceId' | 'supplierRenewalId' | 'audience'>>,
  ctx: z.RefinementCtx,
): void {
  if (v.targetType === 'service' && (!v.serviceId || v.supplierRenewalId)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['serviceId'], message: 'Wskaż umowę' });
  }
  if (v.targetType === 'supplier_renewal' && (!v.supplierRenewalId || v.serviceId)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['supplierRenewalId'], message: 'Wskaż koszt dostawcy' });
  }
  if (v.targetType === 'supplier_renewal' && v.audience === 'client') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['audience'],
      message: 'Koszt dostawcy przypomina tylko zespołowi',
    });
  }
}

export const reminderRuleFieldsShape = reminderRuleFieldsBase;
export const reminderRuleFieldsSchema = reminderRuleFieldsBase.superRefine(reminderRuleRefinement);

export interface DefaultReminderRule {
  audience: ReminderAudience;
  offsetsDays: number[];
  channel: ReminderChannel;
  maxRepeats: number | null;
  /** opis do UI (Figma: karta zasobu → Przypomnienia) */
  label: string;
}

/**
 * Domyślne reguły umowy utrzymania (MODULE_UTRZYMANIE §3; Figma „Przypomnienia”):
 * do opiekuna 30 i 7 dni przed, do klienta 30 dni przed (mail przez Akceptacje), po terminie co 7 dni najwyżej 3 razy.
 */
export const DEFAULT_SERVICE_REMINDER_RULES: readonly DefaultReminderRule[] = [
  {
    audience: 'owner',
    offsetsDays: [-30, -7],
    channel: 'inbox',
    maxRepeats: null,
    label: 'Do Ciebie: 30 i 7 dni przed',
  },
  {
    audience: 'client',
    offsetsDays: [-30],
    channel: 'email_approval',
    maxRepeats: null,
    label: 'Do klienta: 30 dni przed, mail przez Akceptacje',
  },
  {
    audience: 'owner',
    offsetsDays: [7, 14, 21],
    channel: 'inbox',
    maxRepeats: 3,
    label: 'Po terminie: co 7 dni, najwyżej 3 razy',
  },
];

/** Domyślne reguły kosztu dostawcy (domena, hosting): tylko do zespołu, 30 i 7 dni przed. */
export const DEFAULT_SUPPLIER_REMINDER_RULES: readonly DefaultReminderRule[] = [
  {
    audience: 'owner',
    offsetsDays: [-30, -7],
    channel: 'inbox',
    maxRepeats: null,
    label: 'Do Ciebie: 30 i 7 dni przed',
  },
];

// ── Sejf dostępów (metadane; sekret osobno, nigdy w listach) ───────────────
/** Maska sekretu w UI: stała liczba kropek (nie zdradza długości). */
export const CREDENTIAL_MASK = '••••••••••';

export const credentialFieldsSchema = z.object({
  assetId: idSchema,
  label: nameSchema,
  panelUrl: urlSchema.nullable().optional(),
  login: z.string().trim().max(200).nullable().optional(),
  /** link do zewnętrznego menedżera haseł zamiast sekretu */
  externalRef: z.string().trim().max(2048).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type CredentialFields = z.infer<typeof credentialFieldsSchema>;

/** Sekret (tylko wejście zapisu; nigdy w wyjściu list ani w audycie). */
export const credentialSecretSchema = z.string().min(1, { message: 'Podaj sekret' }).max(4096);

// ── Daty i statusy liczone (Europe/Warsaw) ──────────────────────────────────

/** Liczba dni do terminu w dniach kalendarzowych (ujemna = po terminie). Daty "YYYY-MM-DD". */
export function daysUntil(today: string, due: string): number {
  const [ya, ma, da] = today.split('-').map(Number) as [number, number, number];
  const [yb, mb, db] = due.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86_400_000);
}

/**
 * Status płatności widoczny w UI: oczekująca albo zafakturowana po terminie (`due_date` < dziś w Europe/Warsaw)
 * = „po terminie”. Zapisany status się nie zmienia (bez utraty informacji „zafakturowana”).
 */
export function effectivePaymentStatus(
  status: RenewalPaymentStatus,
  dueDate: string,
  today: string,
): RenewalPaymentStatus {
  if ((status === 'pending' || status === 'invoiced') && dueDate < today) return 'overdue';
  return status;
}

/** Kanał zgodny z adresatem: do klienta tylko mail przez Akceptacje, do zespołu Dziś / Inbox / push. */
export function reminderChannelAllowed(audience: ReminderAudience, channel: ReminderChannel): boolean {
  return audience === 'client' ? channel === 'email_approval' : channel !== 'email_approval';
}

// ── Wejścia: zasoby ─────────────────────────────────────────────────────────

export const assetListInput = z
  .object({
    type: assetTypeSchema.optional(),
    clientId: idSchema.optional(),
    q: z.string().trim().max(200).optional(),
    includeArchived: z.boolean().default(false),
  })
  .default({});
export type AssetListInput = z.input<typeof assetListInput>;

export const assetIdInput = z.object({ id: idSchema });

/**
 * Umowa utrzymania zakładana razem z zasobem (Figma „Dodaj zasób” → „Płatność od klienta”):
 * usługa `kind: 'maintenance'` „Utrzymanie <nazwa>”. Kwota wymaga flagi „Widzi stawki i kwoty”.
 */
export const assetContractSchema = z.object({
  price: amountSchema,
  currency: currencySchema.default('PLN'),
  cycle: billingCycleSchema.default('year'),
  nextPaymentDate: localDateSchema,
  /** początek umowy; domyślnie dziś */
  periodStart: localDateSchema.nullable().optional(),
  remindOwner: z.boolean().default(true),
  remindClient: z.boolean().default(true),
});
export type AssetContractInput = z.input<typeof assetContractSchema>;

export const createAssetInput = hostedAssetFieldsSchema
  .extend({ contract: assetContractSchema.nullable().optional() })
  .superRefine((v, ctx) => {
    if (v.contract && v.serviceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contract'],
        message: 'Zasób ma już umowę: wybierz umowę albo podaj płatność',
      });
    }
  });
export type CreateAssetInput = z.input<typeof createAssetInput>;

export const updateAssetInput = hostedAssetFieldsSchema.partial().extend({ id: idSchema, version: versionSchema });
export type UpdateAssetInput = z.input<typeof updateAssetInput>;

export const archiveAssetInput = z.object({
  id: idSchema,
  version: versionSchema,
  archived: z.boolean().default(true),
});

// ── Wejścia: elementy, koszty dostawców, płatności, reguły ─────────────────

export const createComponentInput = assetComponentFieldsSchema;
export const updateComponentInput = assetComponentFieldsSchema
  .omit({ assetId: true })
  .partial()
  .extend({ id: idSchema, version: versionSchema });
export type UpdateComponentInput = z.input<typeof updateComponentInput>;

export const createSupplierRenewalInput = supplierRenewalFieldsSchema.extend({
  /** domyślne przypomnienie do zespołu 30 i 7 dni przed */
  reminders: z.boolean().default(true),
});
export type CreateSupplierRenewalInput = z.input<typeof createSupplierRenewalInput>;

export const updateSupplierRenewalInput = supplierRenewalFieldsSchema
  .omit({ assetId: true })
  .partial()
  .extend({ id: idSchema, version: versionSchema });
export type UpdateSupplierRenewalInput = z.input<typeof updateSupplierRenewalInput>;

export const archiveSupplierRenewalInput = z.object({
  id: idSchema,
  version: versionSchema,
  archived: z.boolean().default(true),
});

/** Odnowienie u dostawcy opłacone: data przesuwa się o cykl. */
export const markSupplierRenewedInput = z.object({ id: idSchema, version: versionSchema });

export const listPaymentsInput = z.object({ serviceId: idSchema });
/** Bieżąca płatność z umowy (okres od `next_payment_date`), idempotentnie. */
export const generatePaymentInput = z.object({ serviceId: idSchema });
export const markPaymentInvoicedInput = z.object({ id: idSchema, version: versionSchema });
export const markPaymentPaidInput = z.object({
  id: idSchema,
  version: versionSchema,
  /** dzień wpłaty (Europe/Warsaw); domyślnie dziś */
  paidOn: localDateSchema.optional(),
});

export const listReminderRulesInput = z
  .object({ serviceId: idSchema.optional(), supplierRenewalId: idSchema.optional() })
  .refine((v) => Boolean(v.serviceId) !== Boolean(v.supplierRenewalId), {
    message: 'Wskaż umowę albo koszt dostawcy',
    path: ['serviceId'],
  });

function reminderChannelRefinement(
  v: { audience?: ReminderAudience; channel?: ReminderChannel },
  ctx: z.RefinementCtx,
): void {
  if (v.audience && v.channel && !reminderChannelAllowed(v.audience, v.channel)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['channel'],
      message:
        v.audience === 'client' ? 'Do klienta tylko mail przez Akceptacje' : 'Mail przez Akceptacje tylko do klienta',
    });
  }
}

export const createReminderRuleInput = reminderRuleFieldsShape.superRefine((v, ctx) => {
  reminderRuleRefinement(v, ctx);
  reminderChannelRefinement(v, ctx);
});
export type CreateReminderRuleInput = z.input<typeof createReminderRuleInput>;

export const updateReminderRuleInput = reminderRuleFieldsShape
  .pick({ audience: true, offsetsDays: true, channel: true, maxRepeats: true, enabled: true })
  .partial()
  .extend({ id: idSchema, version: versionSchema })
  .superRefine(reminderChannelRefinement);
export type UpdateReminderRuleInput = z.input<typeof updateReminderRuleInput>;

/** `date` (testy i ręczne uruchomienie): dzień, dla którego liczymy przypomnienia; nie z przyszłości. */
export const runRemindersInput = z.object({ date: localDateSchema.optional() }).default({});

export const upcomingRenewalsInput = z.object({ days: z.number().int().min(1).max(365).default(30) }).default({});

// ── Akceptacja: mail z przypomnieniem do klienta ────────────────────────────

export const MAINTENANCE_RENEWAL_EMAIL = 'maintenance.renewal_email';

/**
 * Wysoki priorytet akceptacji przypomnienia o płatności (push przy domyślnym „Tylko wysoki priorytet”, MVP §14):
 * tylko PO TERMINIE płatności (DECISIONS Fala 3, rozstrzygnięcia: „przypomnienia o płatności po terminie”; zastępuje
 * wcześniejsze „≤ 7 dni przed terminem”). Przed terminem — normalny; akcja wygasająca w ciągu 12 h i tak dostaje
 * wysoki priorytet od zadania `approvals.priority`.
 */
export function renewalEmailPriority(daysLeft: number): 'normal' | 'high' {
  return daysLeft < 0 ? 'high' : 'normal';
}

/** Priorytet przypomnienia o płatności na dany dzień (termin YYYY-MM-DD minął = wysoki); zadanie co godzinę. */
export function renewalEmailPriorityOn(dueDate: string, today: string): 'normal' | 'high' {
  return dueDate < today ? 'high' : 'normal';
}

/** Payload akceptacji `maintenance.renewal_email` (wysyłka po zatwierdzeniu przez osobę z „Wysyła maile”). */
export const renewalEmailPayloadSchema = z.object({
  to: emailSchema,
  toName: z.string().trim().max(200).nullable().optional(),
  subject: z.string().trim().min(1).max(300),
  body: z.string().min(1).max(20_000),
  serviceId: idSchema,
  clientId: idSchema,
  dueDate: localDateSchema,
  amount: amountSchema.nullable(),
  currency: currencySchema.default('PLN'),
});
export type RenewalEmailPayload = z.infer<typeof renewalEmailPayloadSchema>;

// ── Wyjścia ─────────────────────────────────────────────────────────────────

export interface MaintenanceClientRef {
  id: string;
  name: string;
  color: string | null;
}

/**
 * Najbliższa płatność / odnowienie zasobu (kolumny „Następna płatność” i „Kwota / rok”):
 * `service` — klient płaci za umowę; `supplier` — my płacimy dostawcy; `component` — element (np. w pakiecie).
 * Bez „Widzi stawki i kwoty” pola kwot są pominięte.
 */
export interface AssetRenewal {
  source: 'service' | 'supplier' | 'component';
  date: string;
  /** dni do terminu (Europe/Warsaw), ujemne = po terminie */
  daysLeft: number;
  overdue: boolean;
  amount?: string | null;
  amountPerYear?: string | null;
  currency: string;
  cycle: BillingCycle | null;
  inPackage: boolean;
}

export interface AssetListItem {
  id: string;
  version: number;
  type: AssetType;
  name: string;
  address: string | null;
  client: MaintenanceClientRef;
  projectId: string | null;
  projectName: string | null;
  serviceId: string | null;
  provider: string | null;
  server: string | null;
  status: AssetStatus;
  /** „Alarm” z symulowanego monitora (otwarty incydent w trybie symulacji) — UI pokazuje „Symulacja” */
  alarmSimulated: boolean;
  renewal: AssetRenewal | null;
  archivedAt: Date | null;
}

export interface AssetDto extends AssetListItem {
  tech: string | null;
  repoUrl: string | null;
  monitorId: string | null;
  notesMd: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface AssetComponentDto {
  id: string;
  version: number;
  assetId: string;
  kind: AssetComponentKind;
  description: string | null;
  provider: string | null;
  expiresOn: string | null;
  included: boolean;
}

export interface SupplierRenewalDto {
  id: string;
  version: number;
  assetId: string;
  componentId: string | null;
  provider: string;
  amount?: string;
  currency: string;
  cycle: BillingCycle;
  renewsOn: string;
  /** kotwica cyklu (dzień miesiąca); następny termin: `nextCycleDate(renewsOn, cycle, anchorDay)` */
  anchorDay: number | null;
  daysLeft: number;
  notes: string | null;
  archivedAt: Date | null;
}

export interface RenewalPaymentDto {
  id: string;
  version: number;
  serviceId: string;
  periodStart: string;
  periodEnd: string;
  amount?: string;
  currency: string;
  /** zapisany status */
  status: RenewalPaymentStatus;
  /** status w UI (po terminie liczone w Europe/Warsaw) */
  effectiveStatus: RenewalPaymentStatus;
  dueDate: string;
  billingDraftId: string | null;
  paidAt: Date | null;
}

export interface ReminderRuleDto {
  id: string;
  version: number;
  targetType: ReminderTargetType;
  serviceId: string | null;
  supplierRenewalId: string | null;
  audience: ReminderAudience;
  offsetsDays: number[];
  channel: ReminderChannel;
  maxRepeats: number | null;
  enabled: boolean;
  lastFiredAt: Date | null;
}

/** Ustalenia umowy zasobu do podglądu w trybie częściowym (bez kwot, warunków rozliczenia i płatności). */
export interface AssetAgreementsDto {
  serviceName: string;
  descriptionMd: string | null;
  hoursPoolSeconds: number | null;
  periodStart: string | null;
  status: ServiceDto['status'];
}

export interface AssetDetail {
  asset: AssetDto;
  /** osoba może edytować zasób (moduł Utrzymanie = Edycja + zakres z edycją) */
  canEdit: boolean;
  /** umowa zasobu; `null`, gdy brak albo poza zakresem osoby (`serviceHidden`) */
  service: ServiceDto | null;
  serviceHidden: boolean;
  /**
   * Ustalenia umowy (zakres, pula godzin, od kiedy), gdy sama umowa jest poza zakresem osoby — tryb częściowy klienta
   * (dostęp do zasobu albo projektu): bez ceny, warunków rozliczenia, płatności i przypomnień (DECISIONS [P]
   * „Rozstrzygnięcia pytań z Fali 1”). `null`, gdy umowa jest widoczna w całości albo jej nie ma.
   */
  agreements: AssetAgreementsDto | null;
  components: AssetComponentDto[];
  supplierRenewals: SupplierRenewalDto[];
  payments: RenewalPaymentDto[];
  reminderRules: ReminderRuleDto[];
  /** mail z przypomnieniem do klienta czeka w Akceptacjach */
  pendingClientReminder: { approvalId: string } | null;
}

/**
 * Kwota na rok per waluta (kwot w różnych walutach nie sumujemy): `perYear` w walucie głównej `currency` (PLN, gdy
 * ma kwoty), pozostałe waluty w `otherAmounts` (tylko przy kilku walutach). Bez „Widzi stawki i kwoty” pól nie ma.
 */
export interface MaintenancePerYear {
  perYear?: string;
  currency?: string;
  otherAmounts?: CurrencyAmount[];
}

export interface MaintenanceOverview {
  renewalsSoon: { count: number; first: { assetId: string; name: string; daysLeft: number } | null };
  /** przychód z umów utrzymania; kwota pominięta bez „Widzi stawki i kwoty” */
  revenue: { contracts: number } & MaintenancePerYear;
  /** koszty dostawców (hosting, domeny); kwota pominięta bez „Widzi stawki i kwoty” */
  supplierCosts: { count: number } & MaintenancePerYear;
  alarms: { count: number; first: { assetId: string; name: string; simulated: boolean } | null };
}

export interface MaintenanceFormOptions {
  clients: Array<
    MaintenanceClientRef & {
      /** klient widoczny tylko jako karta (dostęp przez projekt albo zasób) */
      partial: boolean;
      projects: { id: string; name: string }[];
      services: { id: string; name: string; kind: string }[];
    }
  >;
  /** miejsca, gdzie stoją zasoby („Zenbox s55”) */
  locations: { provider: string; server: string | null }[];
}

export interface UpcomingRenewal {
  kind: 'service' | 'supplier_renewal';
  id: string;
  /** np. „helpmycar.pl” (zasób umowy) albo „Domena helpmycar.pl” */
  title: string;
  /** np. „Utrzymanie helpmycar.pl” albo „u dostawcy: Zenbox” */
  subtitle: string;
  client: MaintenanceClientRef;
  assetId: string | null;
  dueDate: string;
  daysLeft: number;
  overdue: boolean;
  amount?: string | null;
  currency: string;
  /** mail do klienta czeka w Akceptacjach */
  pendingClientReminder: boolean;
}

export interface RunRemindersResult {
  date: string;
  fired: Array<{
    ruleId: string;
    targetType: ReminderTargetType;
    targetId: string;
    dueDate: string;
    offsetDays: number;
    audience: ReminderAudience;
    result: 'event' | 'approval' | 'missing_contact';
    eventId: string | null;
    approvalId: string | null;
  }>;
  /** reguły pominięte przez błąd (reszta przebiegu działa dalej) */
  failed: Array<{ ruleId: string; error: string }>;
}
